import React from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs } from 'antd';
import { collect } from 'collect.js';
import SalesReport from '../../../components/team/reports/SalesReport';
import GoLiveConversionReport from '../../../components/team/reports/GoLiveConversionReport';
import MrrReport from '../../../components/team/reports/MrrReport';
import CollectedCommissionReport from '../../../components/team/reports/CollectedCommissionReport';
import useHelpers from '../../../hooks/context/useHelpers';
import usePermissions, { ManagerPermission } from '../../../hooks/auth/usePermissions';

export const Tab = {
  General: 'general',
  SignedLive: 'signed_live',
  MRR: 'mrr',
  CollectedCommission: 'collected_commission',
};

export default function ReportsView() {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:Team:Reports' });

  const { can } = usePermissions();
  const { getActiveTab, setActiveTab } = useHelpers();

  const visibleTabs = collect([Tab.General, Tab.MRR])
    .when(can(ManagerPermission.ReadSalesTeamStats), (collection) => collection.push(Tab.CollectedCommission))
    // Opening the tab and seeing the whole team in it are two different permissions:
    // a reseller holds only the first, so GoLiveConversionReport scopes them to their
    // own row (FOO-8186). FullAccess is the stronger of the two and implies the tab,
    // so a manager granted only it per-row keeps what they were given.
    .when(
      can(ManagerPermission.ReadSignedLiveReports) || can(ManagerPermission.FullAccessSignedLiveReports),
      (collection) => collection.push(Tab.SignedLive),
    )
    .toArray();

  const activeTab = getActiveTab('team.reports', visibleTabs[0] ?? Tab.General);
  const _activeTab = visibleTabs.includes(activeTab) ? activeTab : (visibleTabs[0] ?? Tab.General);

  const content = {
    [Tab.General]: () => <SalesReport />,
    [Tab.SignedLive]: () => <GoLiveConversionReport />,
    [Tab.MRR]: () => <MrrReport />,
    [Tab.CollectedCommission]: () => <CollectedCommissionReport />,
  }[_activeTab];

  return (
    <>
      <Tabs
        activeKey={_activeTab}
        items={visibleTabs.map((item) => ({
          key: item,
          label: t(`tabs.${item}`),
        }))}
        onChange={(tab) => setActiveTab(tab, 'team.reports')}
      />

      <div className="mt-lg">{content()}</div>
    </>
  );
}
